import React from 'react';

import {
  useGeoReference,
  countryOptions,
  subdivisionOptions,
  subdivisionLabel,
  validatePostcode,
  countryHasPostcode,
  findCountry
} from './geoReference';

export function useAddressGeo({
  country,
  subdivision,
  postCode,
  onChange,
  fieldNames = {},
  enabled = {}
}) {
  const {
    country: countryEnabled = true,
    subdivision: subdivisionEnabled = true,
    postCode: postCodeEnabled = true
  } = enabled;
  const {
    country: countryField = 'country',
    subdivision: subdivisionField = 'state',
    postCode: postCodeField = 'postCode'
  } = fieldNames;

  const { reference, isLoading, isError } = useGeoReference();

  const countries = React.useMemo(() => countryOptions(reference), [reference]);

  const countryCode = React.useMemo(() => {
    if (!country) return '';
    const match = (reference?.countries || []).find(
      (c) => c.name === country || c.code === String(country).toUpperCase()
    );
    return match ? match.code : '';
  }, [reference, country]);

  const subdivisions = React.useMemo(
    () => subdivisionOptions(reference, countryCode),
    [reference, countryCode]
  );

  const countryMeta = findCountry(reference, countryCode);

  const hasSubdivisionList = Array.isArray(subdivisions);

  const subdivisionSelectOptions = React.useMemo(
    () => (subdivisions || []).map((s) => ({ value: s.label, label: s.label })),
    [subdivisions]
  );

  const postcodeCheck = React.useMemo(
    () => validatePostcode(reference, countryCode, postCode),
    [reference, countryCode, postCode]
  );

  const hasPostcode = countryHasPostcode(reference, countryCode);

  const handleCountryChange = (nextCountry) => {
    const patch = { [countryField]: nextCountry || '' };
    if (nextCountry !== country) {
      patch[subdivisionField] = '';
    }
    onChange(patch);
  };

  const handleSubdivisionChange = (value) => {
    onChange({ [subdivisionField]: value || '' });
  };

  const handlePostCodeChange = (value) => {
    onChange({ [postCodeField]: value ?? '' });
  };

  const postCodeError =
    postCodeEnabled && Boolean(postCode) && !postcodeCheck.valid;

  const postCodeHelperText = !postCodeError
    ? ''
    : postcodeCheck.generic
    ? "That doesn't look like a postcode"
    : `Expected format: ${postcodeCheck.expectedFormat}${
        postcodeCheck.example ? ` (e.g. ${postcodeCheck.example})` : ''
      }`;

  const hasUnrecognisedCountry =
    countryEnabled && Boolean(country) && !countryCode && !isLoading;

  const hasUnrecognisedSubdivision =
    subdivisionEnabled &&
    Boolean(subdivision) &&
    hasSubdivisionList &&
    !subdivisions.some((s) => s.label === subdivision) &&
    !isLoading;

  return {
    // reference state
    isLoading,
    isError,

    // country
    countryOptions: countries,
    countryCode,
    hasUnrecognisedCountry,
    handleCountryChange,

    // subdivision
    subdivisionOptions: subdivisionSelectOptions,
    hasSubdivisionList,
    hasUnrecognisedSubdivision,
    subdivisionLabel: subdivisionLabel(reference, countryCode),
    handleSubdivisionChange,

    // postcode
    hasPostcode,
    postCodeError,
    postCodeHelperText,
    postcodeCheck,
    handlePostCodeChange,

    // misc
    countryMeta,
    reference,

    validate() {
      const errors = {};
      if (postCodeEnabled && postCode && !postcodeCheck.valid) {
        errors[postCodeField] = postCodeHelperText || 'Invalid postcode';
      }
      return errors;
    }
  };
}

export default useAddressGeo;
